"use client";

import { useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  CheckLines,
  CramerSteps,
  EliminationSteps,
  SystemGraph,
} from "@/components/tools/algebra/system-parts";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type ReadSystem,
  readSystem,
  type SystemSolution,
  solveSystem,
} from "@/lib/tool-kit/algebra/system";
import { decimalMarkOf } from "@/lib/tool-kit/math/locale";
import {
  formatPolynomial,
  formatStyled,
  type NumberStyle,
  type Rational,
  toNumberR,
} from "@/lib/tool-kit/math/rational";

/** English strings. A translated content file overrides these through `ui`. */
export const SYSTEM_OF_EQUATIONS_SOLVER_STRINGS = {
  equations: "Equations — one per line",
  equationsHint:
    "Two or three unknowns, up to four equations. Use the same letters in every equation, for example 2x + 3y = 7.",
  examplesLabel: "Try an example",
  exampleLabels: ["2 × 2", "3 × 3", "No solution", "Infinitely many"],
  examples: [
    "2x + 3y = 7\nx − y = −4",
    "x + y + z = 6\n2y + 5z = −4\n2x + 5y − z = 27",
    "x + y = 2\nx + y = 5",
    "x + y = 2\n2x + 2y = 4",
  ],
  results: "Solution",
  unknown: "{variable}",
  type: "Type of system",
  kinds: {
    unique: "One solution",
    none: "No solution",
    infinite: "Infinitely many solutions",
  },
  copy: "Copy solution",
  copied: "Copied!",
  eq: "E{n}",
  stepsHeading: "Elimination, step by step",
  stepStart: "Start with the system.",
  stepScale:
    "Multiply {row} by {factor} so every coefficient is a whole number.",
  stepSwap: "Swap {a} and {b} so the pivot term is not zero.",
  stepCombine: "Eliminate {variable} from {row}: new {row} = {expression}.",
  stepReduce: "Divide the new equation by {divisor} to keep the numbers small.",
  stepDivide: "Divide {row} by {divisor} to get {variable} on its own.",
  stepTrivial:
    "{row} now reads 0 = 0: it was a combination of the other equations and adds nothing new.",
  stepFalse:
    "{row} now reads 0 = {value}, which is false, so the system has no solution.",
  noSolution:
    "The equations contradict each other: eliminating the unknowns leaves a false statement. No values of the unknowns satisfy every equation at once. With two unknowns, the lines are parallel and never meet.",
  infinite:
    "There are fewer independent equations than unknowns, so the solutions form a line (or a plane). Let {free} stand for any number; then every solution is:",
  freeNames: ["t", "s"],
  checkHeading: "Check the answer",
  checkLine: "{row}: {working} = {total}",
  cramerHeading: "Cramer's rule",
  cramerIntro:
    "D is the determinant of the coefficients; each other determinant replaces one column of D with the constants. With D ≠ 0 each unknown is its determinant divided by D.",
  cramerResult:
    "{variable} = {numerator} / D = {value} / {denominator} = {answer}",
  cramerZero:
    "D = 0, so Cramer's rule does not apply. Elimination above shows whether the system has no solution or infinitely many.",
  graphHeading: "Graph",
  graphTitle:
    "Each equation drawn as a line; a solution is where the lines meet",
  point: "({x}, {y})",
  downloadPng: "Download graph (PNG)",
  enter: "Enter two or three equations, one per line.",
  errors: {
    empty: "Enter two or three equations, one per line.",
    "too-few": "A system needs at least two equations, one per line.",
    "too-many-equations": "Use at most {token} equations.",
    "too-many-unknowns": "Use at most {token} different unknowns.",
    "too-long": "That equation is too long. Shorten it and try again.",
    character:
      "The character “{token}” is not allowed. Use digits, letters for the unknowns, + − × / and brackets.",
    unexpected:
      "Something is out of place near “{token}”. Check for a missing operator.",
    end: "An equation ends too soon. Check for a trailing operator.",
    bracket: "The brackets do not match.",
    "divide-by-variable":
      "Dividing by an unknown makes the equation nonlinear. Divide only by numbers.",
    "divide-by-zero": "Division by zero is not allowed.",
    exponent: "Powers must be whole numbers.",
    "too-large": "That expression is too large to expand.",
    "no-relation": "Equation {token} needs an equals sign.",
    "many-relations": "Equation {token} has more than one equals sign.",
    "many-variables": "Too many different letters.",
    "missing-side": "Both sides of an equals sign need something on them.",
    nonlinear:
      "Equation {token} is not linear: unknowns multiplied together or raised to a power are not allowed here.",
    "no-unknowns": "Use letters such as x and y for the unknowns.",
  },
} as const;

type Strings = typeof SYSTEM_OF_EQUATIONS_SOLVER_STRINGS;

const DECIMAL_INPUT = /[.,]/;

function generalLines(
  system: ReadSystem,
  solution: SystemSolution,
  strings: Strings,
  style: NumberStyle,
  mark: string
): string[] {
  const names = strings.freeNames;
  return system.variables.map((variable, column) => {
    const g = solution.general[column];
    const terms = [
      { coefficient: g.constant, variable: "" },
      ...g.params.map((coefficient, k) => ({
        coefficient,
        variable: names[k] ?? `t${k + 1}`,
      })),
    ];
    return `${variable} = ${formatPolynomial(terms, style, mark)}`;
  });
}

function Outcome({
  system,
  solution,
  strings,
  style,
  mark,
}: {
  system: ReadSystem;
  solution: SystemSolution;
  strings: Strings;
  style: NumberStyle;
  mark: string;
}) {
  const fmt = useToolNumber();
  const ctx = { system, solution, strings, style, mark };
  const show = (value: Rational) => formatStyled(value, style, mark);
  const answers =
    solution.outcome === "unique"
      ? system.variables.map((v, i) => `${v} = ${show(solution.values[i])}`)
      : [];
  const general =
    solution.outcome === "infinite"
      ? generalLines(system, solution, strings, style, mark)
      : [];
  return (
    <>
      <StatGrid>
        {solution.outcome === "unique"
          ? system.variables.map((variable, i) => (
              <StatCard
                emphasis
                key={variable}
                label={
                  solution.values[i].den === BigInt(1)
                    ? t(strings.unknown, { variable })
                    : `${t(strings.unknown, { variable })} ≈ ${fmt(toNumberR(solution.values[i]), { maximumFractionDigits: 5 })}`
                }
                value={show(solution.values[i])}
              />
            ))
          : null}
        <StatCard
          label={strings.type}
          value={strings.kinds[solution.outcome]}
        />
      </StatGrid>
      {solution.outcome === "none" ? (
        <div className={RESULT_CARD}>
          <p className="text-muted-foreground text-sm">{strings.noSolution}</p>
        </div>
      ) : null}
      {solution.outcome === "infinite" ? (
        <div className={`${RESULT_CARD} space-y-2`}>
          <p className="text-muted-foreground text-sm">
            {t(strings.infinite, {
              free: solution.free
                .map((_, k) => strings.freeNames[k] ?? `t${k + 1}`)
                .join(", "),
            })}
          </p>
          <pre className="font-mono text-foreground text-sm">
            {general.join("\n")}
          </pre>
        </div>
      ) : null}
      {answers.length > 0 ? (
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copy}
          small
          text={answers.join(", ")}
        />
      ) : null}
      <h3 className={WIDGET_H3}>{strings.stepsHeading}</h3>
      <EliminationSteps {...ctx} />
      {solution.outcome === "unique" ? (
        <>
          <h3 className={WIDGET_H3}>{strings.checkHeading}</h3>
          <CheckLines {...ctx} />
        </>
      ) : null}
      {solution.cramer ? (
        <>
          <h3 className={WIDGET_H3}>{strings.cramerHeading}</h3>
          <CramerSteps {...ctx} />
        </>
      ) : null}
      {system.variables.length === 2 ? (
        <>
          <h3 className={WIDGET_H3}>{strings.graphHeading}</h3>
          <SystemGraph {...ctx} />
        </>
      ) : null}
    </>
  );
}

function Results({ text, strings }: { text: string; strings: Strings }) {
  const fmt = useToolNumber();
  const mark = decimalMarkOf(fmt);
  const read = readSystem(text);
  if (!read.ok) {
    const template = strings.errors[read.error.code] ?? strings.errors.empty;
    return (
      <ErrorNotice>{t(template, { token: read.error.token })}</ErrorNotice>
    );
  }
  const style: NumberStyle = DECIMAL_INPUT.test(text) ? "decimal" : "fraction";
  const solution = solveSystem(read.system);
  return (
    <Outcome
      mark={mark}
      solution={solution}
      strings={strings}
      style={style}
      system={read.system}
    />
  );
}

export default function SystemOfEquationsSolverWidget() {
  const strings = useToolUi(SYSTEM_OF_EQUATIONS_SOLVER_STRINGS);
  const [text, setText] = useState<string>(strings.examples[0]);
  return (
    <>
      <TextAreaField
        hint={strings.equationsHint}
        id="ses-equations"
        label={strings.equations}
        onChange={setText}
        rows={4}
        value={text}
      />
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-muted-foreground text-sm">
          {strings.examplesLabel}
        </span>
        {strings.examples.map((example, i) => (
          <button
            className={BUTTON_SMALL}
            key={example}
            onClick={() => setText(example)}
            type="button"
          >
            {strings.exampleLabels[i]}
          </button>
        ))}
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {text.trim() === "" ? (
          <Notice>{strings.enter}</Notice>
        ) : (
          <Results strings={strings} text={text} />
        )}
      </section>
    </>
  );
}
